/**
 * @fileoverview BookTour.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential {t('forms.and')} proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information {t('forms.and')} shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Navigate, Link, useLocation } from 'react-router-dom'
import SEO from '../components/ui/SEO'
import DynamicImage from '../components/ui/DynamicImage'
import { apiFetch } from '../utils/api'

function BookTour() {
  const { t } = useTranslation();
  const { tourId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedTier = searchParams.get('tier') || 'basic';

  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);

  const [formData, setFormData] = useState({
    title: 'Mr',
    name: '',
    email: '',
    phoneCode: '+94',
    phone: '',
    country: 'United States',
    languages: [],
    arrivalDate: '',
    arrivalTime: '',
    departureDate: '',
    departureTime: '',
    travelers: {
      adults: 1,
      children: 0,
      seniors: 0,
      infants: 0
    },
    specialNotes: ''
  });

  const popularCountryCodes = [
    { code: '+94', name: 'LK' }, { code: '+1', name: 'US/CA' }, { code: '+44', name: 'UK' }, 
    { code: '+61', name: 'AU' }, { code: '+49', name: 'DE' }, { code: '+33', name: 'FR' },
    { code: '+81', name: 'JP' }, { code: '+86', name: 'CN' }, { code: '+91', name: 'IN' },
    { code: '+971', name: 'AE' }
  ];
  const popularCountries = [
    'United States', 'United Kingdom', 'Australia', 'Germany', 'France', 'Canada', 
    'Japan', 'China', 'India', 'United Arab Emirates', 'Sri Lanka'
  ];

  const [activeStep, setActiveStep] = useState(1);
  const [passengerDetails, setPassengerDetails] = useState([]);
  const [skipPassengerDetails, setSkipPassengerDetails] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const preparePassengerDetails = () => {
    const targetList = [];
    const adults = formData.travelers.adults || 0;
    const children = formData.travelers.children || 0;
    const seniors = formData.travelers.seniors || 0;
    const infants = formData.travelers.infants || 0;

    const findExisting = (ageType, indexInType) => {
      return passengerDetails.find(p => p.ageType === ageType && p.indexInType === indexInType);
    };

    for (let i = 0; i < adults; i++) {
      const existing = findExisting('adult', i);
      targetList.push({
        name: existing ? existing.name : '',
        passport: existing ? existing.passport : '',
        ageType: 'adult',
        indexInType: i,
        label: `Adult Guest ${i + 1}`
      });
    }
    for (let i = 0; i < children; i++) {
      const existing = findExisting('kid', i);
      targetList.push({
        name: existing ? existing.name : '',
        passport: existing ? existing.passport : '',
        ageType: 'kid',
        indexInType: i,
        label: `Child Guest ${i + 1}`
      });
    }
    for (let i = 0; i < seniors; i++) {
      const existing = findExisting('senior', i);
      targetList.push({
        name: existing ? existing.name : '',
        passport: existing ? existing.passport : '',
        ageType: 'senior',
        indexInType: i,
        label: `Senior Guest ${i + 1}`
      });
    }
    for (let i = 0; i < infants; i++) {
      const existing = findExisting('infant', i);
      targetList.push({
        name: existing ? existing.name : '',
        passport: existing ? existing.passport : '',
        ageType: 'infant',
        indexInType: i,
        label: `Infant Guest ${i + 1}`
      });
    }
    setPassengerDetails(targetList);
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.name.trim()) { alert('Please enter your full name.'); return false; }
      if (!formData.email.trim() || !formData.email.includes('@')) { alert('Please enter a valid email address.'); return false; }
      if (!formData.phone.trim()) { alert('Please enter your phone number.'); return false; }
      if (!formData.country.trim()) { alert('Please enter your nationality.'); return false; }
    }
    if (step === 2) {
      if (!formData.arrivalDate) { alert('Please select arrival date.'); return false; }
      if (!formData.departureDate) { alert('Please select departure date.'); return false; }
      if (new Date(formData.departureDate) < new Date(formData.arrivalDate)) {
        alert('Departure date cannot be before arrival date.');
        return false;
      }
    }
    if (step === 3) {
      const adults = formData.travelers.adults || 0;
      const children = formData.travelers.children || 0;
      const seniors = formData.travelers.seniors || 0;
      const infants = formData.travelers.infants || 0;
      if (adults + children + seniors + infants === 0) {
        alert('Please select at least 1 traveler.');
        return false;
      }
    }
    if (step === 4) {
      if (!skipPassengerDetails) {
        for (let i = 0; i < passengerDetails.length; i++) {
          const p = passengerDetails[i];
          if (!p.name.trim()) {
            alert(`Please enter the name for traveler #${i + 1} (${p.label}).`);
            return false;
          }
          if (!p.passport.trim()) {
            alert(`Please enter the passport number for traveler #${i + 1} (${p.label}).`);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleStepClick = (stepId) => {
    if (stepId < activeStep) {
      setActiveStep(stepId);
    } else if (stepId > activeStep) {
      for (let s = activeStep; s < stepId; s++) {
        if (!validateStep(s)) return;
        if (s === 3) {
          preparePassengerDetails();
        }
      }
      setActiveStep(stepId);
    }
  };

  const handleNextStep = () => {
    if (!validateStep(activeStep)) return;
    if (activeStep === 3) {
      preparePassengerDetails();
    }
    setActiveStep(p => Math.min(5, p + 1));
  };

  const getMinArrivalDate = () => {
      const defaultMin = new Date();
      defaultMin.setDate(defaultMin.getDate() + 20);
      let minDate = defaultMin;
      if (tour?.availableFrom) {
          const availFrom = new Date(tour.availableFrom);
          if (availFrom > minDate) minDate = availFrom;
      }
      return minDate.toISOString().split('T')[0];
  };

  const getMaxArrivalDate = () => {
      if (tour?.availableTo) {
          return new Date(tour.availableTo).toISOString().split('T')[0];
      }
      return undefined;
  };

  const getMinDepartureDate = () => {
      if (formData.arrivalDate) {
          const d = new Date(formData.arrivalDate);
          d.setDate(d.getDate() + 1);
          return d.toISOString().split('T')[0];
      }
      return getMinArrivalDate();
  };

  const getMaxDepartureDate = () => {
      if (tour?.availableTo) {
          return new Date(tour.availableTo).toISOString().split('T')[0];
      }
      return undefined;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);

    apiFetch(`/tours/${tourId}`)
      .then(res => {
        if (res && res.data) {
          const t = res.data;
          setTour({
            id: t._id,
            name: t.title,
            price: t.price,
            duration: `${t.duration} Days | ${t.duration - 1} Nights`,
            image: (t.image && typeof t.image === 'object' && (t.image.l || t.image.h)) 
              ? { l: t.image.l || t.image.h, h: t.image.h || t.image.l }
              : { l: (t.images && t.images[0]) || t.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800', h: (t.images && t.images[0]) || t.image || 'https://images.unsplash.com/photo-1546708973-b339540b5162?w=800' },
            experiences: t.itinerary && t.itinerary.length > 0 ? t.itinerary.map(item => item.experience).filter(Boolean) : [],
            itinerary: t.itinerary || [],
            includes: t.includes || [],
            excludes: t.excludes || [],
            availableFrom: t.availableFrom,
            availableTo: t.availableTo
          });
        } else {
          throw new Error('No data');
        }
      })
      .catch(() => {
        setTour(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tourId]);

  if (loading) {
    return (
      <div className='flex flex-col w-full min-h-screen justify-center items-center pt-24 md:pt-32'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-KITH-primary'></div>
      </div>
    );
  }

  if (!tour) {
    return <Navigate to="/tour-plans" />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLanguageChange = (lang) => {
    setFormData(prev => {
      const languages = prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang];
      return { ...prev, languages };
    });
  };

  const updateTraveler = (type, delta) => {
    setFormData(prev => ({
      ...prev,
      travelers: {
        ...prev.travelers,
        [type]: Math.max(0, prev.travelers[type] + delta)
      }
    }));
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!agreedToTerms) {
      alert("Please agree to the Terms of Use {t('forms.and')} Privacy Policy before proceeding.");
      return;
    }
    setSubmitting(true);

    // Format fields
    const nameParts = formData.name.trim().split(/\s+/);
    const fname = nameParts[0] || '';
    const lname = nameParts.slice(1).join(' ') || '.';

    const adults = formData.travelers.adults || 0;
    const children = formData.travelers.children || 0;
    const seniors = formData.travelers.seniors || 0;
    const infants = formData.travelers.infants || 0;
    const numberOfPax = adults + children + seniors + infants;

    const details = [];
    if (skipPassengerDetails) {
      for (let i = 0; i < adults; i++) details.push({ name: `Adult Guest ${i+1}`, ageType: 'adult', age: 30 });
      for (let i = 0; i < children; i++) details.push({ name: `Child Guest ${i+1}`, ageType: 'kid', age: 8 });
      for (let i = 0; i < seniors; i++) details.push({ name: `Senior Guest ${i+1}`, ageType: 'senior', age: 65 });
      for (let i = 0; i < infants; i++) details.push({ name: `Infant Guest ${i+1}`, ageType: 'infant', age: 1 });
    } else {
      passengerDetails.forEach(p => {
        details.push({
          name: p.name.trim(),
          ageType: p.ageType,
          age: p.ageType === 'adult' ? 30 : p.ageType === 'kid' ? 8 : p.ageType === 'senior' ? 65 : 1,
          passport: p.passport.trim()
        });
      });
    }

    const isMongoId = /^[0-9a-fA-F]{24}$/.test(tour.id);
    const basePrice = selectedTier === 'premium' && tour.premiumPrice ? tour.premiumPrice : tour.price;
    const totalPrice = basePrice * (adults + seniors + children * 0.75);

    const payload = {
      customer: {
        title: formData.title,
        fname,
        lname,
        phone: `${formData.phoneCode} ${formData.phone}`,
        email: formData.email,
        country: formData.country,
        languages: formData.languages
      },
      tourPackage: isMongoId ? tour.id : undefined,
      tourTier: selectedTier,
      pricing: {
        totalPrice,
        depositPaid: 0,
        balanceDue: totalPrice,
        currency: 'USD',
        tax: 0,
        discount: 0
      },
      travelers: {
        numberOfPax,
        details
      },
      status: 'pending',
      arrival: {
        date: formData.arrivalDate ? new Date(formData.arrivalDate) : undefined,
        time: formData.arrivalTime || undefined
      },
      departure: {
        date: formData.departureDate ? new Date(formData.departureDate) : undefined,
        time: formData.departureTime || undefined
      },
      specialNotes: formData.specialNotes,
      includes: tour.includes || [],
      excludes: tour.excludes || [],
      itinerary: tour.itinerary ? tour.itinerary.map(day => ({
        day: day.day,
        experiences: day.experience ? [day.experience] : (day.activities || [])
      })) : []
    };

    apiFetch('/bookings', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (res && res.success && res.data) {
          setSuccessBooking(res.data);
        } else {
          throw new Error('Submit failed');
        }
      })
      .catch((err) => {
        alert(err.message || 'Unable to submit booking request. Please try again.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const steps = [
    { id: 1, title: t('forms.personal'), icon: 'fi-br-user' },
    { id: 2, title: t('forms.dates'), icon: 'fi-br-calendar' },
    { id: 3, title: t('forms.travelers'), icon: 'fi-br-users' },
    { id: 4, title: t('forms.passengers'), icon: 'fi-br-id-card' },
    { id: 5, title: t('forms.confirm'), icon: 'fi-br-check' }
  ];

  if (successBooking) {
    return (
      <div className='flex flex-col w-full min-h-screen pt-24 md:pt-32 pb-20 bg-gray-50/50 justify-center items-center px-4'>
        <SEO title="Booking Successful" />
        <div className='max-w-2xl w-full bg-white rounded-[3rem] shadow-2xl p-8 md:p-12 border border-gray-100 text-center flex flex-col gap-6 items-center animate-fadeIn'>
          <div className='w-20 h-20 bg-green-50 rounded-full flex items-center justify-center text-green-500 shadow-md mb-2'>
            <i className='fi fi-br-check-circle text-4xl mt-1'></i>
          </div>
          <h2 className='text-3xl font-black text-gray-800 uppercase tracking-tight'>Booking Confirmed!</h2>
          <p className='text-gray-500 font-medium max-w-md'>
            Thank you, {formData.title} {formData.name.split(' ')[0]}! Your booking request for <span className='text-gray-800 font-bold'>{tour.name}</span> has been received {t('forms.and')} is set to pending status.
          </p>

          <div className='w-full bg-gray-50 p-6 rounded-2xl border border-gray-100 flex flex-col gap-3 my-4 relative overflow-hidden'>
            <div className='absolute top-0 left-0 w-1 h-full bg-KITH-primary'></div>
            <span className='text-[10px] font-black text-gray-400 uppercase tracking-widest'>Your Confirmation Number</span>
            <span className='text-3xl font-black text-KITH-primary tracking-widest'>{successBooking.confirmationNumber}</span>
            <p className='text-xs font-bold text-gray-500 mt-2'>
              <i className='fi fi-br-shield-check text-KITH-primary mr-2'></i>
              Do not share your Tour Number with anyone or any third party. You can use this number to securely track your tour from the <Link to={`/my-tour/${successBooking.confirmationNumber}`} className="text-KITH-primary underline">My Tour</Link> page.
            </p>
          </div>

          <div className='flex flex-col sm:flex-row gap-4 w-full justify-center'>
            <Link 
              to={`/my-tour/${successBooking.confirmationNumber}`}
              className='px-8 py-4 bg-KITH-primary text-white font-bold rounded-2xl hover:bg-KITH-primary-dark transition-all shadow-md text-sm whitespace-nowrap'
            >
              Track Your Tour
            </Link>
            <Link 
              to="/"
              className='px-8 py-4 bg-gray-100 text-gray-600 font-bold rounded-2xl hover:bg-gray-200 transition-all text-sm whitespace-nowrap'
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col w-full min-h-screen pt-24 md:pt-32 pb-20 bg-gray-50/50'>
      <SEO title={`Book ${tour.name}`} />

      <div className='px-[5%] md:px-[10%] max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12'>
        
        {/* Left Column: Form */}
        <div className='lg:col-span-8 flex flex-col gap-10'>
            <div className='bg-white rounded-[3rem] shadow-2xl p-8 md:p-12 border border-gray-100'>
                
                {/* Stepper */}
                <div className='flex justify-between items-center mb-12 relative'>
                    <div className='absolute top-1/2 left-0 w-full h-0.5 bg-gray-100 -translate-y-1/2'></div>
                    <div className='absolute top-1/2 left-0 h-0.5 bg-KITH-primary -translate-y-1/2 transition-all duration-500' style={{width: `${((activeStep-1)/(steps.length-1))*100}%`}}></div>
                    {steps.map(step => (
                        <div key={step.id} onClick={() => handleStepClick(step.id)} className='relative z-10 flex flex-col items-center gap-2 cursor-pointer'>
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${activeStep >= step.id ? 'bg-KITH-primary text-white shadow-lg scale-110' : 'bg-white text-gray-300 border border-gray-100'}`}>
                                <i className={`fi ${step.icon} text-xs`}></i>
                            </div>
                            <span className={`text-[8px] font-black uppercase tracking-widest ${activeStep >= step.id ? 'text-KITH-primary' : 'text-gray-300'}`}>{step.title}</span>
                        </div>
                    ))}
                </div>

                <form onSubmit={(e) => e.preventDefault()} className='flex flex-col gap-8'>
                    {/* Step 1: Personal */}
                    <div className={`${activeStep === 1 ? 'flex' : 'hidden'} flex-col gap-8 animate-fadeIn`}>
                        <h2 className='text-xl font-black text-gray-800 uppercase tracking-tight '>{t('forms.contact_info')}</h2>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                            <div className='flex flex-col gap-3 group md:col-span-2'>
                                <label className='text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1'>{t('forms.title')}</label>
                                <div className='flex gap-4'>
                                    {['Mr', 'Mrs', 'Ms', 'Dr'].map(t => (
                                        <button 
                                            key={t} type="button"
                                            onClick={() => setFormData(prev => ({...prev, title: t}))}
                                            className={`px-6 py-2 rounded-xl font-bold text-sm border-2 transition-all ${formData.title === t ? 'bg-KITH-primary border-KITH-primary text-white shadow-md' : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'}`}
                                        >{t}</button>
                                    ))}
                                </div>
                            </div>
                            <div className='flex flex-col gap-2'>
                                <label className='text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1'>{t('forms.full_name')}</label>
                                <input type="text" name="name" value={formData.name} required className='w-full bg-gray-50 px-5 py-4 rounded-2xl outline-none font-bold text-gray-700' placeholder="John Doe" onChange={handleInputChange} />
                            </div>
                            <div className='flex flex-col gap-2'>
                                <label className='text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1'>{t('forms.email')}</label>
                                <input type="email" name="email" value={formData.email} required className='w-full bg-gray-50 px-5 py-4 rounded-2xl outline-none font-bold text-gray-700' placeholder="john@example.com" onChange={handleInputChange} />
                            </div>
                            <div className='flex flex-col gap-2 group'>
                                <label className='text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1'>{t('forms.phone')}</label>
                                <div className='flex gap-2 w-full'>
                                    <select 
                                        name="phoneCode" 
                                        value={formData.phoneCode} 
                                        onChange={handleInputChange}
                                        className='w-1/3 md:w-1/4 bg-gray-50 px-3 py-4 rounded-2xl outline-none font-bold text-gray-700 shadow-inner appearance-none cursor-pointer border-r-8 border-transparent'
                                    >
                                        {popularCountryCodes.map(c => (
                                            <option key={c.code} value={c.code}>{c.code} {c.name}</option>
                                        ))}
                                    </select>
                                    <input type="tel" name="phone" value={formData.phone} required className='flex-1 bg-gray-50 px-5 py-4 rounded-2xl outline-none font-bold text-gray-700' placeholder="234 567 890" onChange={handleInputChange} />
                                </div>
                            </div>
                            <div className='flex flex-col gap-2 group'>
                                <label className='text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1'>{t('forms.nationality')}</label>
                                <select 
                                    name="country" 
                                    value={formData.country} 
                                    onChange={handleInputChange}
                                    className='w-full bg-gray-50 px-5 py-4 rounded-2xl outline-none font-bold text-gray-700 shadow-inner appearance-none cursor-pointer border-r-8 border-transparent'
                                >
                                    {popularCountries.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Step 2: Dates */}
                    <div className={`${activeStep === 2 ? 'flex' : 'hidden'} flex-col gap-8 animate-fadeIn`}>
                         <h2 className='text-xl font-black text-gray-800 uppercase tracking-tight '>{t('forms.your_schedule')}</h2>
                         <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
                            <div className='flex flex-col gap-4 p-6 bg-gray-50 rounded-3xl'>
                                <span className='text-[10px] font-black text-KITH-primary uppercase tracking-widest'>{t('forms.arrival_details')}</span>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-xs text-gray-500 font-bold uppercase tracking-widest'>{t('forms.arrivalDate')}</label>
                                    <input type="date" name="arrivalDate" min={getMinArrivalDate()} max={getMaxArrivalDate()} value={formData.arrivalDate} className='bg-white px-4 py-3 rounded-xl font-bold border border-transparent focus:border-KITH-primary outline-none text-gray-700' onChange={handleInputChange} />
                                </div>
                                <input type="time" name="arrivalTime" value={formData.arrivalTime} className='bg-white px-4 py-3 rounded-xl font-bold border border-transparent focus:border-KITH-primary outline-none text-gray-700' onChange={handleInputChange} />
                            </div>
                            <div className='flex flex-col gap-4 p-6 bg-gray-50 rounded-3xl'>
                                <span className='text-[10px] font-black text-KITH-primary uppercase tracking-widest'>{t('forms.departure_details')}</span>
                                <div className='flex flex-col gap-2'>
                                    <label className='text-xs text-gray-500 font-bold uppercase tracking-widest'>{t('forms.departureDate')}</label>
                                    <input type="date" name="departureDate" min={getMinDepartureDate()} max={getMaxDepartureDate()} value={formData.departureDate} className='bg-white px-4 py-3 rounded-xl font-bold border border-transparent focus:border-KITH-primary outline-none text-gray-700' onChange={handleInputChange} />
                                </div>
                                <input type="time" name="departureTime" value={formData.departureTime} className='bg-white px-4 py-3 rounded-xl font-bold border border-transparent focus:border-KITH-primary outline-none text-gray-700' onChange={handleInputChange} />
                            </div>
                         </div>
                    </div>

                    {/* Step 3: Travelers */}
                    <div className={`${activeStep === 3 ? 'flex' : 'hidden'} flex-col gap-8 animate-fadeIn`}>
                         <h2 className='text-xl font-black text-gray-800 uppercase tracking-tight '>{t('forms.travel_party')}</h2>
                         <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            {Object.entries(formData.travelers).map(([type, count]) => (
                                <div key={type} className='flex items-center justify-between p-4 bg-gray-50 rounded-2xl'>
                                    <span className='font-bold text-gray-700 capitalize text-sm'>{type}</span>
                                    <div className='flex items-center gap-3'>
                                        <button type="button" onClick={() => updateTraveler(type, -1)} className='w-8 h-8 rounded-lg bg-white border border-gray-200 text-gray-500 font-bold hover:bg-gray-100 transition-all flex items-center justify-center'>-</button>
                                        <span className='font-black text-gray-800 text-base min-w-[20px] text-center'>{count}</span>
                                        <button type="button" onClick={() => updateTraveler(type, 1)} className='w-8 h-8 rounded-lg bg-KITH-primary text-white font-bold hover:bg-KITH-primary-dark transition-all flex items-center justify-center'>+</button>
                                    </div>
                                </div>
                            ))}
                         </div>
                    </div>

                    {/* Step 4: Passenger Details */}
                    <div className={`${activeStep === 4 ? 'flex' : 'hidden'} flex-col gap-8 animate-fadeIn`}>
                        <div className="flex justify-between items-center ">
                            <h2 className='text-xl font-black text-gray-800 uppercase tracking-tight'>{t('forms.traveler_details')}</h2>
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input 
                                    type="checkbox" 
                                    checked={skipPassengerDetails} 
                                    onChange={(e) => setSkipPassengerDetails(e.target.checked)} 
                                    className="rounded border-gray-300 text-KITH-primary focus:ring-KITH-primary w-4 h-4"
                                />
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{t('forms.skip_details')}</span>
                            </label>
                        </div>
                        
                        {!skipPassengerDetails ? (
                            <div className="flex flex-col gap-6 max-h-[400px] overflow-y-auto pr-2 scrollbarThin">
                                {passengerDetails.map((p, idx) => (
                                    <div key={idx} className="p-6 bg-gray-50 rounded-3xl border border-gray-100 flex flex-col gap-4">
                                        <div className="flex justify-between items-center border-b border-gray-200/60 pb-2">
                                            <span className="text-xs font-black text-KITH-primary uppercase tracking-widest">{p.label}</span>
                                            <span className="text-[10px] font-bold bg-white text-gray-500 px-3 py-1 rounded-full border border-gray-100 uppercase">
                                                {t('forms.category')} {p.ageType === 'adult' ? 'Adult' : p.ageType === 'kid' ? 'Child' : p.ageType === 'senior' ? 'Senior' : 'Infant'}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="flex flex-col gap-2">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{t('forms.full_name')}</label>
                                                <input 
                                                    type="text" 
                                                    value={p.name}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setPassengerDetails(prev => prev.map((item, i) => i === idx ? { ...item, name: val } : item));
                                                    }}
                                                    placeholder="As in Passport"
                                                    className="w-full bg-white px-4 py-3 rounded-xl border border-transparent focus:border-KITH-primary outline-none font-bold text-gray-700 text-sm shadow-sm"
                                                />
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{t('forms.passport_number')}</label>
                                                <input 
                                                    type="text" 
                                                    value={p.passport}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setPassengerDetails(prev => prev.map((item, i) => i === idx ? { ...item, passport: val } : item));
                                                    }}
                                                    placeholder="Passport Number"
                                                    className="w-full bg-white px-4 py-3 rounded-xl border border-transparent focus:border-KITH-primary outline-none font-bold text-gray-700 text-sm shadow-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-8 bg-gray-50 rounded-3xl border border-dashed border-gray-200 text-center flex flex-col items-center gap-3">
                                <i className="fi fi-br-shield-check text-3xl text-gray-400"></i>
                                <p className="text-sm font-bold text-gray-500">{t('forms.skip_msg_1')}</p>
                                <p className="text-xs text-gray-400">{t('forms.skip_msg_2')}</p>
                            </div>
                        )}
                    </div>

                    {/* Step 5: Preferences */}
                    <div className={`${activeStep === 5 ? 'flex' : 'hidden'} flex-col gap-8 animate-fadeIn`}>
                        <h2 className='text-xl font-black text-gray-800 uppercase tracking-tight '>{t('forms.preferences_notes')}</h2>
                        <div className="flex flex-col gap-4">
                            <label className='text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1'>{t('forms.pref_languages')}</label>
                            <div className='flex flex-wrap gap-4'>
                                 {['English', 'German', 'French', 'Chinese', 'Spanish'].map(lang => (
                                    <button 
                                        key={lang} type="button"
                                        onClick={() => handleLanguageChange(lang)}
                                        className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${formData.languages.includes(lang) ? 'bg-KITH-primary border-KITH-primary text-white shadow-md' : 'bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100'}`}
                                    >{lang}</button>
                                 ))}
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                             <label className='text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1'>{t('forms.special_notes')}</label>
                             <textarea 
                                 name="specialNotes" 
                                 value={formData.specialNotes} 
                                 className='w-full min-h-[120px] bg-gray-50 p-6 rounded-2xl outline-none font-bold text-gray-700 focus:bg-gray-50/20 focus:ring-2 focus:ring-KITH-primary' 
                                 placeholder={t('forms.special_notes_placeholder')} 
                                 onChange={handleInputChange}
                             ></textarea>
                        </div>
                    </div>

                    {/* Step 5: Policies Agreement */}
                    <div className={`${activeStep === 5 ? 'flex' : 'hidden'} flex-col gap-6 animate-fadeIn mt-4`}>
                        <label className="flex items-start gap-3 cursor-pointer select-none bg-KITH-primary/5 p-4 rounded-2xl border border-KITH-primary/20">
                            <input 
                                type="checkbox" 
                                checked={agreedToTerms} 
                                onChange={(e) => setAgreedToTerms(e.target.checked)} 
                                className="mt-1 rounded border-gray-300 text-KITH-primary focus:ring-KITH-primary w-5 h-5"
                                required
                            />
                            <span className="text-sm text-gray-700 leading-relaxed font-medium">
                                I have read {t('forms.and')} agree to the <Link to="/terms-and-conditions" target="_blank" className="text-KITH-primary hover:underline font-bold">{t('forms.terms_use')}</Link> {t('forms.and')} <Link to="/privacy-policy" target="_blank" className="text-KITH-primary hover:underline font-bold">{t('forms.privacy_policy')}</Link>.
                            </span>
                        </label>
                    </div>

                    <div className='flex justify-between items-center mt-6 border-t border-gray-100 pt-6'>
                        <button type="button" onClick={() => setActiveStep(p => Math.max(1, p-1))} className={`text-[10px] font-black uppercase tracking-widest transition-all ${activeStep === 1 ? 'opacity-0 cursor-default' : 'text-gray-400 hover:text-gray-600'}`}>{t('forms.back')}</button>
                        {activeStep < 5 ? (
                            <button type="button" onClick={handleNextStep} className='px-10 py-4 bg-KITH-primary text-white font-black uppercase tracking-widest text-[10px] rounded-xl shadow-lg hover:bg-KITH-primary-dark transition-all'>{t('forms.next_step')}</button>
                        ) : (
                            <button type="button" onClick={handleSubmit} disabled={submitting} className='px-10 py-4 bg-KITH-primary text-white font-black uppercase tracking-widest text-[10px] rounded-xl shadow-lg hover:bg-KITH-primary-dark transition-all disabled:bg-gray-300 disabled:cursor-not-allowed'>
                              {submitting ? t('forms.confirming') : t('forms.confirm_booking')}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>

        {/* Right Column: Summary Card */}
        <div className='lg:col-span-4 flex flex-col gap-6 sticky top-32 h-fit'>
            <div className='bg-white rounded-[2.5rem] shadow-xl overflow-hidden border border-gray-100'>
                <div className='h-48 relative'>
                    <DynamicImage HighImage={tour.image.h} LowImage={tour.image.l} />
                    <div className='absolute inset-0 bg-gradient-to-t from-black/80 to-transparent'></div>
                    <div className='absolute bottom-6 left-6 right-6'>
                        <h3 className='text-white font-black text-xl leading-tight'>{tour.name}</h3>
                    </div>
                </div>
                <div className='p-8 flex flex-col gap-6'>
                    <div className='flex justify-between items-center pb-4 border-b border-gray-50'>
                        <span className='text-[10px] font-black text-gray-400 uppercase tracking-widest'>Price / Person</span>
                        <span className='text-xl font-black text-KITH-primary'>USD {tour.price.toLocaleString()}</span>
                    </div>
                    <div className='flex justify-between items-center pb-4 border-b border-gray-50'>
                        <span className='text-[10px] font-black text-gray-400 uppercase tracking-widest'>Duration</span>
                        <span className='text-sm font-black text-gray-700'>{tour.duration}</span>
                    </div>
                    <div className='flex flex-col gap-3'>
                        <span className='text-[10px] font-black text-gray-400 uppercase tracking-widest'>Highlights</span>
                        <div className='flex flex-wrap gap-2'>
                            {tour.experiences.slice(0, 3).map((exp, i) => (
                                <span key={i} className='px-3 py-1 bg-gray-50 rounded-lg text-[9px] font-bold text-gray-500 uppercase'>{exp}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className='bg-KITH-primary/5 p-8 rounded-[2.5rem] border border-KITH-primary/10 flex flex-col gap-4'>
                <h4 className='font-black text-gray-800 text-sm uppercase tracking-tight'>Need Assistance?</h4>
                <p className='text-[10px] text-gray-500 font-medium leading-relaxed'>Our travel experts are ready to help you customize this tour to your liking. Feel free to contact us anytime.</p>
                <Link to="/contact-us" className='text-[10px] font-black text-KITH-primary uppercase tracking-widest hover:underline'>Contact Support →</Link>
            </div>
        </div>

      </div>
    </div>
  )
}

export default BookTour
